const MANAGED_ATTR = 'data-kayicom-seo';
export const SITE_NAME = 'KayiCom';
export const SITE_URL = 'https://kayicom.com';
export const DEFAULT_DESCRIPTION =
  'KayiCom is a global digital marketplace for gift cards, game top-ups, streaming subscriptions, mobile recharge, and digital services with fast delivery and a secure customer experience.';
export const DEFAULT_OG_IMAGE = '/og-share.png?v=2';
export const DEFAULT_OG_IMAGE_WIDTH = '1200';
export const DEFAULT_OG_IMAGE_HEIGHT = '630';

const DIRECT_OG_IMAGE_PREFIXES = [
  `${SITE_URL}/assets/`,
  `${SITE_URL}/og-share`,
  `${SITE_URL}/og-image.png`,
  `${SITE_URL}/favicon`,
];

const needsOgImageProxy = (url) => {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    if (host === 'kayicom.com' || host === 'www.kayicom.com') {
      return parsed.pathname.startsWith('/api/uploads/') || parsed.pathname.startsWith('/uploads/');
    }
    if (host === 'api.kayicom.com' || host === 'kayicom-beta.onrender.com' || host === 'api.g2bulk.com') {
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

export const stripHtmlToText = (value = '') =>
  String(value || '')
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, ' ')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|div|li|h1|h2|h3|h4|h5|h6|section|article|blockquote|tr)>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const truncateText = (value = '', limit = 160) => {
  const cleaned = stripHtmlToText(value);
  if (cleaned.length <= limit) return cleaned;
  return `${cleaned.slice(0, Math.max(0, limit - 3)).trim()}...`;
};

export const absoluteUrl = (path = '/') => {
  const raw = String(path || '').trim();
  if (raw.startsWith('data:')) return absoluteUrl(DEFAULT_OG_IMAGE);
  if (/^https?:\/\//i.test(raw)) return raw;
  const normalizedPath = raw.startsWith('/') ? raw : `/${raw}`;
  return `${SITE_URL}${normalizedPath}`;
};

/** WhatsApp/Facebook crawlers prefer same-origin OG images; proxy external product uploads. */
export const resolveShareImageUrl = (imageUrl) => {
  const raw = String(imageUrl || '').trim();
  if (!raw || raw.startsWith('data:')) return absoluteUrl(DEFAULT_OG_IMAGE);
  const normalized = absoluteUrl(raw);
  if (normalized.includes('/api/og-image?')) return normalized;
  if (DIRECT_OG_IMAGE_PREFIXES.some((prefix) => normalized.startsWith(prefix))) return normalized;
  if (needsOgImageProxy(normalized)) {
    return `${SITE_URL}/api/og-image?src=${encodeURIComponent(normalized)}`;
  }
  return normalized;
};

export const formatPageTitle = (title) => {
  const value = String(title || '').trim();
  if (!value) return `${SITE_NAME} — Digital Marketplace`;
  if (/kayicom/i.test(value)) return value;
  return `${value} | ${SITE_NAME}`;
};

export const deriveProductSeoTitle = (item) =>
  String(item?.seo_title || '').trim() || String(item?.name || '').trim() || 'Product';

export const deriveProductSeoDescription = (item) => {
  const raw =
    String(item?.seo_description || '').trim() ||
    String(item?.description || '').trim();
  return raw
    ? truncateText(raw, 160)
    : truncateText(`Buy ${String(item?.name || 'digital product').trim()} securely on KayiCom.`, 160);
};

export const buildOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: absoluteUrl('/og-image.png'),
  description: DEFAULT_DESCRIPTION,
  areaServed: ['HT', 'DO', 'JM', 'US'],
});

export const buildWebSiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  description: DEFAULT_DESCRIPTION,
  publisher: {
    '@type': 'Organization',
    name: SITE_NAME,
  },
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/products?search={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
});

export const buildBreadcrumbSchema = (items = []) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: absoluteUrl(item.path),
  })),
});

export const buildProductSchema = (product, options = {}) => {
  const name = product?.name || 'Product';
  const description = deriveProductSeoDescription(product);
  const imageUrl = product?.image_url;
  const hasValidImage = imageUrl && /^https?:\/\//i.test(imageUrl);
  const canonicalPath = options.canonicalPath || `/product/${product?.slug || product?.id || ''}`;

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    description,
    ...(hasValidImage ? { image: [resolveShareImageUrl(imageUrl)] } : { image: [absoluteUrl(DEFAULT_OG_IMAGE)] }),
    brand: { '@type': 'Brand', name: SITE_NAME },
    offers: {
      '@type': 'Offer',
      priceCurrency: String(product?.currency || 'USD').toUpperCase(),
      price: Number(product?.price || 0).toFixed(2),
      availability: product?.stock_available !== false
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      url: absoluteUrl(canonicalPath),
      seller: { '@type': 'Organization', name: SITE_NAME },
      itemCondition: 'https://schema.org/NewCondition',
    },
  };
};

const setAttributes = (el, attrs = {}) => {
  Object.entries(attrs).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') {
      el.removeAttribute(key);
    } else {
      el.setAttribute(key, String(value));
    }
  });
};

export const applySeoMetadata = ({
  title,
  description,
  canonicalPath,
  type = 'website',
  image,
  publishedAt,
  modifiedAt,
  tags = [],
  structuredData,
  locale = 'en_US',
  noindex = false,
} = {}) => {
  if (typeof document === 'undefined') return () => {};

  const previousTitle = document.title;
  const touched = [];
  const resolvedTitle = formatPageTitle(title);
  const resolvedDescription = truncateText(description || DEFAULT_DESCRIPTION);
  const canonicalUrl = absoluteUrl(canonicalPath || window.location.pathname || '/');
  const imageUrl = resolveShareImageUrl(image || DEFAULT_OG_IMAGE);

  const upsert = (selector, tagName, attrs) => {
    let el = document.head.querySelector(selector);
    const existed = Boolean(el);
    const previousAttrs = {};

    if (!el) {
      el = document.createElement(tagName);
      document.head.appendChild(el);
    }

    Object.keys(attrs).forEach((key) => {
      previousAttrs[key] = el.getAttribute(key);
    });
    previousAttrs[MANAGED_ATTR] = el.getAttribute(MANAGED_ATTR);

    setAttributes(el, attrs);
    el.setAttribute(MANAGED_ATTR, 'true');
    touched.push({ el, existed, previousAttrs });
  };

  const createManagedMeta = (attrs) => {
    const el = document.createElement('meta');
    setAttributes(el, attrs);
    el.setAttribute(MANAGED_ATTR, 'true');
    document.head.appendChild(el);
    touched.push({ el, existed: false, previousAttrs: {} });
  };

  document.title = resolvedTitle;
  if (document.documentElement) {
    document.documentElement.lang = String(locale || 'en_US').split('_')[0] || 'en';
  }

  upsert('meta[name="description"]', 'meta', { name: 'description', content: resolvedDescription });
  upsert('meta[name="robots"]', 'meta', {
    name: 'robots',
    content: noindex
      ? 'noindex, follow'
      : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
  });
  upsert('link[rel="canonical"]', 'link', { rel: 'canonical', href: canonicalUrl });
  upsert('meta[property="og:site_name"]', 'meta', { property: 'og:site_name', content: SITE_NAME });
  upsert('meta[property="og:title"]', 'meta', { property: 'og:title', content: resolvedTitle });
  upsert('meta[property="og:description"]', 'meta', { property: 'og:description', content: resolvedDescription });
  upsert('meta[property="og:type"]', 'meta', { property: 'og:type', content: type });
  upsert('meta[property="og:url"]', 'meta', { property: 'og:url', content: canonicalUrl });
  upsert('meta[property="og:locale"]', 'meta', { property: 'og:locale', content: locale });
  upsert('meta[property="og:image"]', 'meta', { property: 'og:image', content: imageUrl });
  upsert('meta[property="og:image:secure_url"]', 'meta', { property: 'og:image:secure_url', content: imageUrl });
  upsert('meta[property="og:image:alt"]', 'meta', { property: 'og:image:alt', content: `${SITE_NAME} — Digital Marketplace` });
  upsert('meta[property="og:image:width"]', 'meta', { property: 'og:image:width', content: DEFAULT_OG_IMAGE_WIDTH });
  upsert('meta[property="og:image:height"]', 'meta', { property: 'og:image:height', content: DEFAULT_OG_IMAGE_HEIGHT });
  upsert('meta[property="og:image:type"]', 'meta', { property: 'og:image:type', content: imageUrl.endsWith('.png') ? 'image/png' : 'image/jpeg' });
  upsert('meta[name="twitter:card"]', 'meta', { name: 'twitter:card', content: 'summary_large_image' });
  upsert('meta[name="twitter:title"]', 'meta', { name: 'twitter:title', content: resolvedTitle });
  upsert('meta[name="twitter:description"]', 'meta', { name: 'twitter:description', content: resolvedDescription });
  upsert('meta[name="twitter:image"]', 'meta', { name: 'twitter:image', content: imageUrl });

  if (publishedAt) {
    upsert('meta[property="article:published_time"]', 'meta', {
      property: 'article:published_time',
      content: publishedAt,
    });
  }

  if (modifiedAt) {
    upsert('meta[property="article:modified_time"]', 'meta', {
      property: 'article:modified_time',
      content: modifiedAt,
    });
  }

  tags.filter(Boolean).forEach((tag) => {
    createManagedMeta({ property: 'article:tag', content: tag });
  });

  const schemaPayload = structuredData
    ? (Array.isArray(structuredData) ? structuredData : [structuredData])
    : [];

  schemaPayload.forEach((data, index) => {
    if (!data) return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(data);
    script.setAttribute(MANAGED_ATTR, 'true');
    script.setAttribute('data-kayicom-schema-index', String(index));
    document.head.appendChild(script);
    touched.push({ el: script, existed: false, previousAttrs: {} });
  });

  return () => {
    document.title = previousTitle;
    touched.reverse().forEach(({ el, existed, previousAttrs }) => {
      if (!existed) {
        el.remove();
        return;
      }

      Object.entries(previousAttrs).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          el.removeAttribute(key);
        } else {
          el.setAttribute(key, value);
        }
      });
    });
  };
};

export const buildBlogArticleSchema = (post) => {
  const slug = post?.slug || post?.id || '';
  const canonical = absoluteUrl(`/blog/${slug}`);
  const description = truncateText(post?.seo_description || post?.excerpt || post?.content || '');

  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post?.seo_title || post?.title || 'Blog post',
    description,
    datePublished: post?.published_at || post?.created_at || undefined,
    dateModified: post?.updated_at || post?.published_at || post?.created_at || undefined,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonical,
    },
    author: {
      '@type': 'Organization',
      name: SITE_NAME,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: absoluteUrl('/android-chrome-512x512.png'),
      },
    },
    image: post?.cover_image_url ? [absoluteUrl(post.cover_image_url)] : [absoluteUrl(DEFAULT_OG_IMAGE)],
  };
};
